import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

import { aggridHeaderClass } from "src/components/common/common";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { defaultKSK2080_R01Dto, KSK2080_R01Dto } from "./Dto_KSK2080_R01";
import { useKSK2080_R01Store } from "./Store_KSK2080_R01";
import { formatDate } from "src/utils/string/stringUtil";
import { cellDateComparator, onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";

const COLUMN_STATE_KEY = 'KSK2080_R01-grid-column-state';

interface Props {
	// data: CustomerResponseDto[]
	gridRef: React.RefObject<AgGridReact<KSK2080_R01Dto> | null>;
}

export const Table_KSK2080_R01 = (props: Props) => {

	const { gridRef } = props;

	const { 
		KSK2080_R01List, setKSK2080_R01List,
		selectedKSK2080_R01, setSelectedKSK2080_R01, initSelectedKSK2080_R01,
		editKSK2080_R01, setEditKSK2080_R01,
		columnState, setColumnState,
	} = useKSK2080_R01Store();

	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'noticeNo',
		gridData: KSK2080_R01List,
		defaultGridData: defaultKSK2080_R01Dto,
		selectedRow: selectedKSK2080_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		writerDt: '게시일자', 
		noticeNo: 'No',
		noticeTitle: '제목',
	}

	const getCellStyle = (params: any, key: string) => {
		return onlyTouchGridStyle(
			key,
			'noticeNo',
		)(params);
	}

	const baseColumnDefs: ColDef<KSK2080_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '게시일자', colId: 'writerDt', field: 'writerDt', width: 120,  
			cellStyle: (params) => getCellStyle(params, 'writerDt'),
			
			valueFormatter: (params: any) => {
				if (params.node?.rowPinned === 'bottom') {
					return params.value;
				}
				return params.data?.writerDt ? formatDate(new Date(params.data?.writerDt)) : '';
			},
			comparator: cellDateComparator,
		},
		{
			headerName: '제목', colId: 'noticeTitle', field: 'noticeTitle', width: 250, 
			cellStyle: (params) => getCellStyle(params, 'noticeTitle'),
			 
		},
		{
			headerName: 'No', colId: 'noticeNo', field: 'noticeNo', width: 160, 
			cellStyle: (params) => getCellStyle(params, 'noticeNo'),
			 
		},

	], [hoveredRowIndex, KSK2080_R01List, selectedKSK2080_R01]);



	const columnDefs: ColDef<KSK2080_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<KSK2080_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, columnState, headerNames]);

	
	

	const handleRowClick = useCallback((e: RowClickedEvent<KSK2080_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 계좌와 같은지 확인
		const isSelected = selectedKSK2080_R01?.noticeNo === rowData.noticeNo;
		if (isSelected) {
			setSelectedKSK2080_R01(undefined);
		} else {
			setSelectedKSK2080_R01(rowData);
		}
	}, [selectedKSK2080_R01, setSelectedKSK2080_R01]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		writerDt: KSK2080_R01List.length.toLocaleString() + "건",
		editable: false,
	}]), [KSK2080_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [KSK2080_R01List, ]);

  return (
    <GridContextWrapper<KSK2080_R01Dto>
		gridRef={gridRef}
		paddedGridData={paddedGridData}
		initSelected={initSelectedKSK2080_R01}
		defaultRowData={defaultKSK2080_R01Dto}
		baseColumnDefs={columnDefs}
		setColumnState={setColumnState}
		localStorageKey={COLUMN_STATE_KEY}
		menuItems={undefined}
	>
      <Grid_Touch
        ref={gridRef}
        rowData={paddedGridData}
        columnDefs={columnDefs}			defaultColDef={defaultColDef}
		getRowId={getRowId}
		
        rowClassRules={createRowClassRules<KSK2080_R01Dto>({
			idKey: 'noticeNo',
			selectedList: selectedKSK2080_R01 ? [selectedKSK2080_R01.noticeNo] : [],
			deletedList: []
		})}
        onRowClicked={handleRowClick}
		onCellKeyDown={undefined}
        pinnedBottomRowData={pinnedBottomRow}
                
		// Grid_Select에 공통 로직 처리를 위한 props 전달
		setHoveredRowIndex={setHoveredRowIndex}		//마우스 호버 상태 이벤트를 위한 저장
		columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
		setColumnState={setColumnState}				//컬럼 상태 저장
      />
    </GridContextWrapper>
  )
}